import { Injectable } from '@angular/core';
import { BaseWooCommerceV2ApiService } from './base-woo-commerce-v2-api.service';
import { catchError, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { WooCommerceCartInfo } from '../../../interfaces/woo-commerce/api/cart/cart.interface';
import {
  WooCommercePostCartItemBody,
  WooCommercePutCartItemParameters
} from '../../../interfaces/woo-commerce/cart/cart.interface';
import {
  HappyCartBatchAddItem,
  HappyCartBatchAddRequest,
  HappyCartBatchRemoveRequest,
  HappyCartBatchUpdateRequest,
  WooCommerceBatchRequest
} from '../../../interfaces/woo-commerce/cart/cart-batch.interface';
import { BaseWooCommerceAddressDetails } from '../../../interfaces/woo-commerce/woo-commerce.interface';
import { mapHappyCartToWooCart } from '../../happy/happy-api-mappers';
import {
  HappyCartAddressRequest,
  HappyCartCheckoutInfo,
  HappyCartPrefillNotesResponse,
  HappyCartTotalsResponse,
  HappyCartUpdateRequest,
  HappyCartValidateResponse
} from '../../../interfaces/happy-api/happy-api.interface';

@Injectable({
  providedIn: 'root'
})
export class WooCommerceCartApiService extends BaseWooCommerceV2ApiService {
  private readonly happyCartUrl: string = '/cart';

  getCart(
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    return this.get(this.happyCartUrl).pipe(
      map((cart: any) => mapHappyCartToWooCart(cart))
    );
  }

  /**
   * PUT /cart — persist delivery date, contact info, notes onto the cart.
   * Per Cart-Subs.md §5: these fields live on the cart, not on the checkout payload.
   */
  updateCart(body: HappyCartUpdateRequest): Observable<void> {
    return this.put(`${this.happyCartUrl}`, body).pipe(map(() => void 0));
  }

  setRewardPoints(
    points: number,
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    return this.put(`${this.happyCartUrl}/points`, {
      // The docs show `{ points }`, but the live backend validates `points_to_redeem`.
      points,
      points_to_redeem: points
    }).pipe(
      switchMap(() => this.getCart(shippingAddress))
    );
  }

  /**
   * GET /cart/totals — live totals from the pricing engine.
   *
   * CONFLICT NOTE: Cart-Subs.md §9 lists this as a dedicated endpoint that
   * returns decimal-string amounts.  End-User-API-Guide.md says GET /cart
   * already includes totals inline.  We expose both so the component can
   * call whichever the backend supports.
   */
  getCartTotals(): Observable<HappyCartTotalsResponse> {
    return this.get<any>(`${this.happyCartUrl}/totals`).pipe(
      map((response: any) => response?.data || response || {})
    );
  }

  addCartItem(
    body: WooCommercePostCartItemBody,
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    const payload: Record<string, unknown> = {
      product_id: body.id,
      quantity: body.quantity
    };

    if (body.variant_id != null) {
      payload['variant_id'] = body.variant_id;
    }

    if (body.addons != null) {
      payload['addons'] = body.addons;
    }

    return this.post(`${this.happyCartUrl}/items`, payload).pipe(
      switchMap(() => this.getCart(shippingAddress))
    );
  }

  addCartItemsBatch(
    items: WooCommercePostCartItemBody[],
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    const payload: HappyCartBatchAddRequest = {
      items: items.map((item) => this.mapBatchAddItem(item))
    };

    return this.post(`${this.happyCartUrl}/items/batch`, payload).pipe(
      switchMap(() => this.getCart(shippingAddress))
    );
  }

  updateCartItem(
    payload: WooCommercePutCartItemParameters,
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    return this.put(`${this.happyCartUrl}/items/${payload.key}`, {
      quantity: payload.quantity
    }).pipe(switchMap(() => this.getCart(shippingAddress)));
  }

  updateCartItemsBatch(
    items: WooCommercePutCartItemParameters[],
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    const payload: HappyCartBatchUpdateRequest = {
      items: items.map(({ key, quantity }) => ({
        cart_item_id: Number(key),
        quantity
      }))
    };

    return this.put(`${this.happyCartUrl}/items/batch`, payload).pipe(
      switchMap(() => this.getCart(shippingAddress))
    );
  }

  removeCartItem(
    cartItemId: number,
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    return this.delete(`${this.happyCartUrl}/items/${cartItemId}`).pipe(
      switchMap(() => this.getCart(shippingAddress))
    );
  }

  removeCartItemsBatch(
    cartItemIds: Array<number | string>,
    shippingAddress?: BaseWooCommerceAddressDetails
  ): Observable<WooCommerceCartInfo> {
    const payload: HappyCartBatchRemoveRequest = {
      item_ids: cartItemIds.map((itemId) => Number(itemId))
    };

    return this.delete(`${this.happyCartUrl}/items/batch`, {
      body: payload
    }).pipe(switchMap(() => this.getCart(shippingAddress)));
  }

  batchCartRequests(payload: WooCommerceBatchRequest): Observable<unknown> {
    const requests = payload?.requests || [];
    if (requests.length === 0) {
      return of({ responses: [] });
    }

    const operations = requests.map((request) => {
      const path = request.path || '';
      const method = (request.method || '').toUpperCase();
      const [, queryString = ''] = path.split('?');
      const queryParams = new URLSearchParams(queryString);

      if (path.endsWith('/cart/add-item') && method === 'POST') {
        const addPayload: Record<string, unknown> = {
          product_id: Number(request.body?.id),
          quantity: request.body?.quantity
        };
        if (request.body?.['variant_id'] != null) {
          addPayload['variant_id'] = request.body['variant_id'];
        }
        return this.post(`${this.happyCartUrl}/items`, addPayload);
      }

      if (path.includes('/cart/update-item') && method === 'POST') {
        const itemKey = queryParams.get('key') || '0';
        const quantity = Number(queryParams.get('quantity') || '0');
        return this.put(`${this.happyCartUrl}/items/${Number(itemKey)}`, {
          quantity
        });
      }

      if (path.includes('/cart/remove-item') && method === 'POST') {
        const itemKey = queryParams.get('key') || '0';
        return this.delete(`${this.happyCartUrl}/items/${Number(itemKey)}`);
      }

      return of(null);
    });

    return forkJoin(operations);
  }

  selectShippingRate(
    rateId: string,
    address: Partial<BaseWooCommerceAddressDetails> = {},
    packageId: string = '0'
  ): Observable<WooCommerceCartInfo> {
    const zoneShippingMethodId = Number.parseInt(
      (rateId || '').split(':').pop() || '',
      10
    );
    const payload: Record<string, unknown> = Number.isNaN(
      zoneShippingMethodId
    )
      ? {}
      : { zone_shipping_method_id: zoneShippingMethodId };

    return this.put(`${this.happyCartUrl}/shipping`, payload).pipe(
      switchMap(() => this.getCart())
    );
  }

  getCheckoutInfo(): Observable<HappyCartCheckoutInfo> {
    return this.get<any>(`${this.happyCartUrl}/checkout-info`).pipe(
      map((response: any) => response?.data || response || {})
    );
  }

  /**
   * POST /cart/validate — pre-checkout validation.
   * Cart-Subs.md §8: CHECKOUT mode auto-removes invalid items and returns
   * errors, warnings, and auto_removals arrays.
   *
   * Real response shape:
   *   { status: false, message: "Cart validation failed.", data: { valid: false, errors: [...], warnings: [], auto_removals: [] } }
   *
   * The backend may return HTTP 200 with status:false OR HTTP 422.
   * We normalize both into a HappyCartValidateResponse so the caller
   * always gets a clean object (no unhandled throws for validation failures).
   */
  validateCart(): Observable<HappyCartValidateResponse> {
    return this.post<any>(`${this.happyCartUrl}/validate`, {}).pipe(
      map((response: any) => this.parseValidateResponse(response)),
      catchError((httpError: any) => {
        const body = httpError?.error || {};
        return of(this.parseValidateResponse(body));
      })
    );
  }

  private parseValidateResponse(response: any): HappyCartValidateResponse {
    const data = response?.data || response || {};
    const outerStatus = response?.status;
    return {
      valid: data?.valid ?? (outerStatus === false ? false : true),
      is_valid: data?.valid ?? (outerStatus === false ? false : true),
      errors: data?.errors || [],
      warnings: data?.warnings || [],
      auto_removals: data?.auto_removals || []
    };
  }

  getPrefillNotes(): Observable<HappyCartPrefillNotesResponse> {
    return this.get<any>(`${this.happyCartUrl}/prefill-notes`).pipe(
      map((response: any) => ({
        notes: response?.data?.notes || response?.notes || ''
      }))
    );
  }

  /**
   * PUT /cart/address — upsert billing or shipping address on the cart.
   * Returns void because the caller should re-fetch cart if needed.
   */
  setCartAddress(payload: HappyCartAddressRequest): Observable<void> {
    return this.put(`${this.happyCartUrl}/address`, payload).pipe(
      map(() => void 0)
    );
  }

  /**
   * PUT /cart/shipping — set shipping method or pickup location.
   */
  setCartShipping(payload: {
    zone_shipping_method_id?: number;
    pickup_location_id?: number;
  }): Observable<void> {
    return this.put(`${this.happyCartUrl}/shipping`, payload).pipe(
      map(() => void 0)
    );
  }

  private mapBatchAddItem(
    item: WooCommercePostCartItemBody
  ): HappyCartBatchAddItem {
    const { id, ...rest } = item as WooCommercePostCartItemBody &
      Record<string, unknown>;

    return {
      ...rest,
      product_id: id,
      quantity: item.quantity
    } as HappyCartBatchAddItem;
  }
}
